import type { OptionEntry } from "../schema/formModel.js";
import "./styles.css";

export interface ChoiceOption extends OptionEntry {
  description?: string;
}

export interface ChoiceCardsProps {
  id: string;
  options: ReadonlyArray<ChoiceOption>;
  /** radio: the one selected value; multi: every selected value. */
  selected: ReadonlyArray<string>;
  multi?: boolean;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  onSelect: (value: string, checked: boolean) => void;
}

/**
 * KYC-design — two to four options as CHOICE CARDS (a radio or checkbox each), replacing native
 * radios and short dropdowns. The native input stays in the DOM (visually hidden) so keyboard
 * and assistive technology behave exactly as for a radio group.
 */
export function ChoiceCards({ id, options, selected, multi = false, required, invalid, describedBy, onSelect }: ChoiceCardsProps) {
  const cols = options.length <= 4 ? options.length : 2;
  return (
    <div className={`choice-cards choice-cards-${cols}`} role={multi ? "group" : "radiogroup"} aria-describedby={describedBy}>
      {options.map((opt, index) => {
        const optionId = `${id}__${index}`;
        const isSelected = selected.includes(opt.value);
        return (
          <label
            key={opt.value}
            htmlFor={optionId}
            className={`choice-card${isSelected ? " choice-card-selected" : ""}${invalid && !isSelected ? " choice-card-invalid" : ""}`}
          >
            <input
              id={optionId}
              name={multi ? optionId : id}
              type={multi ? "checkbox" : "radio"}
              value={opt.value}
              required={required && !multi}
              checked={isSelected}
              onChange={(e) => onSelect(opt.value, e.target.checked)}
            />
            <span className={`choice-mark${multi ? " choice-mark-check" : ""}`} aria-hidden="true" />
            <span className="choice-copy">
              <span className="choice-label">{opt.label}</span>
              {opt.description && <span className="choice-description">{opt.description}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}
